import "../../style.scss";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";


// ---- small helpers (no deps) ----
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const hexToRgba = (hex, a = 0.4) => {
    const c = hexToRgb(hex);
    if (!c) return hex; // fallback
    return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
};

const hexToRgb = (hex) => {
    if (!hex) return null;
    let h = hex.replace("#", "").trim();
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return null;
    const num = parseInt(h, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHex = ({ r, g, b }) =>
    `#${[r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, "0")).join("")}`;

const mix = (hexA, hexB, weightB = 0.12) => {
    const a = hexToRgb(hexA);
    const b = hexToRgb(hexB);
    if (!a || !b) return hexA || hexB;
    const wB = clamp(weightB, 0, 1);
    const wA = 1 - wB;
    return rgbToHex({
        r: a.r * wA + b.r * wB,
        g: a.g * wA + b.g * wB,
        b: a.b * wA + b.b * wB,
    });
};

// crude luminance to pick text color
const isDark = (hex) => {
    const c = hexToRgb(hex);
    if (!c) return false;
    const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
    return lum < 0.6;
};

export const CustomLoadingButton = ({
    onClick,
    children,
    startIcon,
    endIcon,
    height,
    width,
    borderRadius = "8px",
    disabled,
    iconPosition = "end",
    currentTheme,
    borderRadiusType = "all",
    startIconColor,
    endIconColor,
    color,                  // main background (hex). If missing uses theme primary
    hoverColor,             // optional explicit hover background
    textColor,              // optional explicit text color
    borderColor,            // optional
    variant = "solid",      // "solid" | "soft" | "ghost",
    tooltip,                // string | undefined
    tooltipPlacement = "bottom",
}) => {
    const [loading, setLoading] = useState(false);
    const isIconOnly = !children;
    const primary = "#965cfa";
    const base = color || primary;

    const computed = useMemo(() => {
        const solidBg = base;
        const softBg = mix(base, "#ffffff", 0.86);

        // tint background (transparent color), text/icon = base
        const tintOpacity = currentTheme === "dark" ? 0.28 : 0.40;
        const tintHoverOpacity = currentTheme === "dark" ? 0.38 : 0.52;

        const bg =
            variant === "ghost" ? "transparent"
                : variant === "soft" ? softBg
                    : variant === "tint" ? hexToRgba(base, tintOpacity)
                        : solidBg;

        const txt =
            textColor ||
            (variant === "tint" ? base : (bg === "transparent" ? (currentTheme === "dark" ? "#fff" : "#0b1220") : (isDark(bg) ? "#fff" : "#0b1220")));

        const hoverBg =
            hoverColor ||
            (variant === "ghost"
                ? mix(base, "#ffffff", currentTheme === "dark" ? 0.14 : 0.18)
                : variant === "soft"
                    ? mix(base, "#ffffff", 0.78)
                    : variant === "tint"
                        ? hexToRgba(base, tintHoverOpacity)
                        : mix(base, "#000000", 0.12));

        const border =
            borderColor ||
            (variant === "soft"
                ? mix(base, "#ffffff", 0.55)
                : variant === "tint"
                    ? hexToRgba(base, currentTheme === "dark" ? 0.28 : 0.22)
                    : "transparent");

        return { bg, hoverBg, txt, border };
    }, [base, variant, hoverColor, textColor, borderColor, currentTheme]);



    const handleClick = async () => {
        try {
            setLoading(true);
            await onClick?.();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const colorizeIcon = (icon, c) =>
        icon ? React.cloneElement(icon, { style: { color: c, ...(icon.props?.style || {}) } }) : null;

    const icon = loading
        ? <CircularProgress size={22} sx={{ color: endIconColor || computed.txt }} />
        : iconPosition === "end"
            ? colorizeIcon(endIcon, endIconColor || computed.txt)
            : colorizeIcon(startIcon, startIconColor || computed.txt);


    const getBorderRadius = () => {
        switch (borderRadiusType) {
            case "left": return `${borderRadius} 0px 0px ${borderRadius}`;
            case "right": return `0px ${borderRadius} ${borderRadius} 0px`;
            case "none": return "0px";
            default: return borderRadius;
        }
    };

    const buttonEl = (
        <span style={{ display: "inline-flex" }}>
            <Button
                onClick={handleClick}
                startIcon={iconPosition === "start" && icon ? icon : null}
                endIcon={iconPosition === "end" && icon ? icon : null}
                disabled={loading || disabled}
                sx={{
                    "&:hover": {
                        backgroundColor: computed.hoverBg,
                        color: computed.txt,
                    },
                    padding: "7px 14px",
                    height: height || "36px",
                    backgroundColor: computed.bg,
                    color: computed.txt,
                    border:
                        computed.border !== "transparent"
                            ? `1px solid ${computed.border}`
                            : "1px solid transparent",
                    fontSize: "14px",
                    borderRadius: getBorderRadius(),
                    width: width || "max-content",
                    cursor: loading || disabled ? "not-allowed" : "pointer",
                    textTransform: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow:
                        variant === "solid"
                            ? "0 6px 14px rgba(2, 6, 23, 0.10)"
                            : "none",
                    whiteSpace: "nowrap",
                }}
            >
                {loading ? "Working..." : children}
            </Button>
        </span>
    );


    return tooltip ? (
        <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            arrow
            disableInteractive={false}
        >
            {buttonEl}
        </Tooltip>
    ) : (
        buttonEl
    );
};

export const CustomButton = ({
    onClick,
    children,
    startIcon,
    height,
    width,
    disabled = false,
}) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            disabled={disabled}
            sx={{
                "&:hover": {
                    backgroundColor: "color-mix(in srgb, var(--primary) 14%, var(--card))",
                    color: "var(--text-soft)",
                    borderColor: "color-mix(in srgb, var(--primary) 35%, var(--border))",
                },
                padding: "5px 15px",
                backgroundColor: "var(--card)",
                color: "var(--text)",
                border: disabled
                    ? "1px solid color-mix(in srgb, var(--border) 70%, transparent)"
                    : "1px solid var(--border)",
                fontWeight: "bold",
                fontSize: "14px",
                alignItems: "center",
                justifyContent: "center",
                display: "flex",
                height: height || "36px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonFilled = ({
    onClick,
    children,
    startIcon,
    height,
    width,
}) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    backgroundColor: "var(--primary-600)",
                    border: "1px solid transparent",
                },
                padding: "5px 15px",
                border: "1px solid transparent",
                fontWeight: "bold",
                fontSize: "14px",
                color: "#fff",
                backgroundColor: "var(--primary)",
                height: height || "36px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const SearchButton = ({
    onClick,
    children,
    startIcon,
    height,
    width,
    themeColor = "var(--primary)",
    borderRadius = "0px 8px 8px 0px",
}) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    backgroundColor: "var(--primary-600)",
                    color: "#fff",
                    border: "none",
                },
                padding: "5px 15px",
                border: "none",
                fontWeight: "bold",
                fontSize: "14px",
                color: "#fff",
                backgroundColor: themeColor,
                height: height || "34px",
                width: width || "max-content",
                borderRadius,
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonGradient = ({
    onClick,
    children,
    startIcon,
    height,
    width,
    borderRadius,
}) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    color: "#fff",
                    backgroundColor: "var(--primary-600)",
                },
                transition: "transform 1s ease",
                padding: "5px 15px",
                fontWeight: "bold",
                fontSize: "14px",
                color: "#fff",
                backgroundColor: "var(--primary)",
                height: height || "36px",
                width: width || "max-content",
                borderRadius: borderRadius || "8px",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomLoadingGradient = ({
    onClick,
    children,
    startIcon,
    height,
    width,
}) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        try {
            setLoading(true);
            await onClick?.();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            onClick={handleClick}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : startIcon}
            disabled={loading}
            sx={{
                "&:hover": {
                    backgroundColor: "var(--primary-600)",
                    color: "#fff",
                },
                padding: "5px 15px",
                backgroundColor: "var(--primary)",
                color: "#fff",
                fontWeight: "bold",
                height: height || "36px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonMenu = ({
    onClick,
    children,
    startIcon,
    currentButton,
    height,
    width,
    currentTheme,
}) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    backgroundColor: "color-mix(in srgb, var(--card) 90%, var(--bg))",
                    color: "var(--text)",
                    borderRadius: "5px",
                },
                padding: "5px 15px",
                backgroundColor: currentButton
                    ? "color-mix(in srgb, var(--card) 90%, var(--bg))"
                    : "transparent",
                fontWeight: currentButton ? "bold" : "500",
                fontSize: "13px",
                color: currentButton ? "var(--text)" : "color-mix(in srgb, var(--text) 55%, transparent)",
                borderRadius: "5px",
                flex: "1",
                height: height || "30px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonMenu_Back = (props) => {
    const { onClick, children, startIcon, currentButton, height, width } = props;
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    backgroundColor: "color-mix(in srgb, var(--card) 90%, var(--bg))",
                    color: "var(--text)",
                    borderRadius: "8px",
                },
                padding: "5px 15px",
                backgroundColor: currentButton
                    ? "color-mix(in srgb, var(--card) 90%, var(--bg))"
                    : "transparent",
                fontWeight: currentButton ? "bold" : "500",
                fontSize: "13px",
                color: currentButton ? "var(--text)" : "color-mix(in srgb, var(--text) 55%, transparent)",
                borderRadius: "8px",
                flex: "1",
                height: height || "30px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};


export const CustomLoadingButtonFilled = ({
    onClick,
    children,
    startIcon,
    height,
    width,
    borderRadius = "8px",
    disabled,
}) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        try {
            if (!onClick) return;
            setLoading(true);
            await onClick();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            onClick={handleClick}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : startIcon}
            disabled={loading || disabled}
            sx={{
                "&:hover": {
                    backgroundColor: "var(--primary-600)",
                    color: "#fff",
                },
                padding: "5px 15px",
                backgroundColor: "var(--primary)",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "15px",
                height: height || "40px",
                borderRadius,
                width: width || "max-content",
                cursor: loading || disabled ? "not-allowed" : "pointer",
                textTransform: "none",
            }}
        >
            {loading ? "Working..." : children}
        </Button>
    );
};

export const CustomButtonSmall = ({
    onClick,
    children,
    startIcon,
    isCurrent = false,
    height,
    width,
    borderRadius = "5px",
    borderRadiusType = "all",
}) => {
    const getButtonStyles = () => {
        if (isCurrent) {
            return {
                backgroundColor: "var(--primary)",
                color: "#fff",
                "&:hover": { backgroundColor: "var(--primary-600)", color: "#fff" },
            };
        }
        return {
            backgroundColor: "color-mix(in srgb, var(--card) 90%, var(--bg))",
            color: "color-mix(in srgb, var(--text) 70%, transparent)",
            "&:hover": { backgroundColor: "color-mix(in srgb, var(--primary) 12%, var(--card))", color: "var(--text)" },
        };
    };

    const getBorderRadius = () => {
        switch (borderRadiusType) {
            case "left":
                return `${borderRadius} 0px 0px ${borderRadius}`;
            case "right":
                return `0px ${borderRadius} ${borderRadius} 0px`;
            case "none":
                return "0px";
            default:
                return borderRadius;
        }
    };

    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                ...getButtonStyles(),
                fontWeight: "bold",
                fontSize: "12px",
                padding: "5px 15px",
                zIndex: 0,
                height: height || "25px",
                width: width || "max-content",
                borderRadius: getBorderRadius(),
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonPlain = ({ onClick, children, startIcon, height, width }) => {
    return (
        <Button
            onClick={onClick}
            startIcon={startIcon}
            sx={{
                "&:hover": {
                    backgroundColor: "color-mix(in srgb, var(--primary) 12%, var(--card))",
                    color: "var(--text)",
                },
                fontWeight: "bold",
                fontSize: "12px",
                color: "var(--text)",
                backgroundColor: "color-mix(in srgb, var(--card) 70%, transparent)",
                padding: "5px 15px",
                height: height || "36px",
                width: width || "max-content",
                textTransform: "none",
            }}
        >
            {children}
        </Button>
    );
};

export const CustomButtonSmallDarkBg = ({
    onClick,
    children,
    startIcon,
    endIcon,
    height,
    width,
    disabled,
    color,
    iconPosition = "start",
    borderRadius = "8px",
    borderRadiusType = "all",
    tooltip,                // string | undefined
    tooltipPlacement = "bottom",
}) => {
    const primary = "#0f172a";
    const base = color || primary;
    const [loading, setLoading] = useState(false);

    const getBorderRadius = () => {
        switch (borderRadiusType) {
            case "left":
                return `${borderRadius} 0px 0px ${borderRadius}`;
            case "right":
                return `0px ${borderRadius} ${borderRadius} 0px`;
            case "none":
                return "0px";
            default:
                return borderRadius;
        }
    };



    const colorizeIcon = (icon, c) =>
        icon ? React.cloneElement(icon, { style: { color: c, ...(icon.props?.style || {}) } }) : null;

    const icon = loading
        ? <CircularProgress size={22} />
        : iconPosition === "end"
            ? colorizeIcon(endIcon, base)
            : colorizeIcon(startIcon, base);

    const handleClick = async () => {
        try {
            setLoading(true);
            await onClick?.();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const buttonEl = (
        <span style={{ display: "inline-flex" }}>
            <Button
                onClick={handleClick}
                startIcon={iconPosition === "start" && icon ? icon : null}
                endIcon={iconPosition === "end" && icon ? icon : null}
                disabled={loading || disabled}
                sx={{
                    "&:hover": {
                        backgroundColor: "color-mix(in srgb, var(--action) 18%, var(--card))",
                        color: "var(--text)",
                    },
                    border: "1px solid color-mix(in srgb, var(--action) 30%, var(--bg))",
                    backgroundColor: "color-mix(in srgb, var(--read-only) 80%, var(--bg))",
                    color: "var(--text)",
                    fontWeight: "bold",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    padding: "5px 10px",
                    height: height || "25px",
                    width: width || "max-content",
                    borderRadius: getBorderRadius(),
                    textTransform: "none",
                }}
            >
                {loading ? "Working..." : children}
            </Button>
        </span>
    );


    return tooltip ? (
        <Tooltip
            title={tooltip}
            placement={tooltipPlacement}
            arrow
            disableInteractive={false}
        >
            {buttonEl}
        </Tooltip>
    ) : (
        buttonEl
    );
};

const CircularButtonStyle = styled(IconButton)(
    ({ width, height, padding, addBorder = true, color }) => ({
        width: width || 40,
        height: height || 40,
        padding: padding || "5px",

        backgroundColor: `color-mix(in srgb, ${color} 10%, var(--bg))`,
        color: color,

        "& svg": {
            color: `${color} !important`,
        },

        "& .MuiSvgIcon-root": {
            color: `${color} !important`,
        },

        border: addBorder
            ? "0.5px solid color-mix(in srgb, var(--border) 50%, var(--bg))"
            : "none",

        "&:hover": {
            backgroundColor: `color-mix(in srgb, ${color} 50%, var(--bg))`,
            color: color,
        },
    })
);

const CircularButton = ({
    onClick,
    icon = <AddIcon />,
    width = 40,
    height = 40,
    padding = "5px",
    addBorder = true,
    color = "var(--primary)"
}) => {
    return (
        <CircularButtonStyle onClick={onClick} width={width} height={height} padding={padding} addBorder={addBorder} color={color}>
            {icon}
        </CircularButtonStyle>
    );
};

export default CircularButton;
